import { toI18N } from "../i18n";

export type AuthErrorCode =
	| "invalid_token"
	| "too_many_attempts"
	| "rate_limited"
	| "needs_invitation"
	| "unknown";

export class AuthApiError extends Error {
	code: AuthErrorCode;
	serverMessage?: string;

	constructor(code: AuthErrorCode, serverMessage?: string) {
		super(serverMessage || code);
		this.code = code;
		this.serverMessage = serverMessage;
	}
}

// Single place that turns an axios failure into a typed error.
// The backend has no machine codes, so known cases are detected by the
// most robust signal available: HTTP status first, then exact strings.
export function normalizeAuthError(err: any): AuthApiError {
	const status = err?.response?.status;
	const data = err?.response?.data;
	const text =
		typeof data?.error === "string"
			? data.error
			: typeof data?.message === "string"
				? data.message
				: undefined;

	if (status === 429) return new AuthApiError("rate_limited", text);
	if (text === "invalid token") return new AuthApiError("invalid_token", text);
	if (text === "too many verification attempts")
		return new AuthApiError("too_many_attempts", text);
	if (text === "New user needs invitation to join")
		return new AuthApiError("needs_invitation", text);
	return new AuthApiError("unknown", text);
}

function capitalize(s: string): string {
	return s.charAt(0).toUpperCase() + s.slice(1);
}

// Display tiers: known code -> i18n; unknown with server text -> capitalized
// server text; otherwise generic. Raw JSON must never reach a toast.
export function authErrorMessage(e: unknown): string {
	const err = e instanceof AuthApiError ? e : new AuthApiError("unknown");
	switch (err.code) {
		case "invalid_token":
			return toI18N("lp_login_otp_error_invalid_code");
		case "too_many_attempts":
			return toI18N("lp_login_otp_error_too_many_attempts");
		case "rate_limited":
			return toI18N("lp_login_otp_error_rate_limited");
		case "needs_invitation":
			// Preserves existing behavior: this exact key is what
			// handleSendEmail shows for the invitation gate today.
			return toI18N("lp_signup_need_community");
		default:
			return err.serverMessage
				? capitalize(err.serverMessage)
				: toI18N("lp_login_otp_error_generic");
	}
}
